/* eslint-disable react/jsx-sort-props */
import React from 'react'
import PropTypes from 'prop-types'
import defaultEmptyStateIcon from 'components/EmptyStateBox/ss_emptydesk_adminx.svg'
import { BodyCopy, HeartIcon } from 'secondstep-components'
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import { toTitleCase } from 'utils/stringHelpers'
import { lessonTitleMap } from 'components/K8LessonsTable/unitAndLessonTitleMap'
import CheckboxDropdownFilter from './CheckboxDropdownFilter'
import SchoolGradeFilter from './SchoolGradeFilter'
import UnitRangeDropdown from './UnitRangeDropdown'
import {
  BigText,
  ClassesTooltip,
  ChevronIcon,
  ClassNameContainer,
  FilterButtonGroup,
  FilterLabel,
  FilterRow,
  FlexContainer,
  GradeTooltip,
  HairlineDivider,
  IconContainer,
  LastLessonNumLine,
  LastLessonStack,
  LastLessonTitleLine,
  LastLessonTooltip,
  LessonProgressTooltip,
  MarkedDoneTooltip,
  OuterCard,
  PillButton,
  ReportTableV2DataTable,
  ResetFiltersLink,
  StyledEmptyStateBox,
  MarkedDoneDate,
  SmallText,
  StatusDot,
  SummaryDivider,
  SummaryHeartIconWrap,
  SummaryRow,
  SummaryStrong,
  SummaryWithIcon,
  TableLastUpdated,
  TableScrollRegion,
  TextContainer,
  TextHiddenOverflow,
  TitleRow,
  TitleText,
  UnitRangePillButton,
} from './component.styles'

dayjs.extend(localizedFormat)

const SummaryHeartIcon = props => (
  <SummaryHeartIconWrap {...props}>
    <HeartIcon />
  </SummaryHeartIconWrap>
)
SummaryHeartIcon.displayName = 'SummaryHeartIcon'

const ReportTableV2 = React.forwardRef(
  (
    {
      activityFilterLabel,
      activityFilterOptions,
      classes,
      classesTooltip,
      displayUnit5Column,
      emptyStateIcon,
      fallbackGradeName,
      gradeFilterGrades,
      gradeTooltip,
      handleRowClick,
      lastLessonTooltip,
      lastUpdatedText,
      markedDoneTooltip,
      numClassesMarkedLastPeriod,
      numClassesNotMarkedLesson,
      onActivityFilterClick,
      onActivityFilterToggle,
      onGradeFilterClick,
      onGradeFilterToggle,
      onResetFilters,
      onUnitRangeChange,
      progressCoreTooltip,
      progressTooltip,
      progressUnit5Tooltip,
      selectedActivityKeys,
      selectedGradeNames,
      showActivityFilter,
      showFilters,
      showGradeFilter,
      showSummary,
      title,
      totalClassesCount,
      unitRangeLabel,
      unitRangeOptions,
    },
    ref,
  ) => {
    const numOfClasses = classes.length

    const unitRangeOptionsList = Array.isArray(unitRangeOptions)
      ? unitRangeOptions
      : []

    const hasMultipleUnitRangeOptions = unitRangeOptionsList.length > 1

    const staticUnitRangeDisplayLabel =
      unitRangeOptionsList.length === 1
        ? unitRangeOptionsList[0]
        : unitRangeLabel

    const hasInlineGradeFilter =
      Array.isArray(gradeFilterGrades) && gradeFilterGrades.length > 0

    const hasInlineActivityFilter =
      Array.isArray(activityFilterOptions) && activityFilterOptions.length > 0

    const showFilterToolbar =
      showFilters &&
      (hasInlineGradeFilter ||
        hasInlineActivityFilter ||
        showGradeFilter ||
        showActivityFilter)

    const selectionSize = sel => {
      if (sel == null) return 0
      if (sel instanceof Set) return sel.size
      if (Array.isArray(sel)) return sel.length
      if (typeof sel?.size === 'number') return sel.size
      return 0
    }

    /** Non-empty selection means the user narrowed grade or activity (defaults are empty sets). */
    const gradeFilterApplied = selectionSize(selectedGradeNames) > 0

    const activityFilterApplied = selectionSize(selectedActivityKeys) > 0

    const showResetFiltersLink =
      showFilterToolbar &&
      typeof onResetFilters === 'function' &&
      (gradeFilterApplied || activityFilterApplied)

    const showNotStartedDot = row => {
      const coreDone = row.lessonsCompletedCore ?? row.lessonsCompleted ?? 0
      const coreTotal = row.lessonCountCore ?? row.lessonCount ?? 0
      return (
        coreDone === 0 && coreTotal > 0 && !row.hasCompletedLessonLastPeriod
      )
    }

    const gradeColumn = {
      title: 'Grade',
      component: (
        <FlexContainer>
          <TextHiddenOverflow>Grade</TextHiddenOverflow>
          {gradeTooltip ? (
            <GradeTooltip content={<BodyCopy>{gradeTooltip}</BodyCopy>}>
              <i className="fas fa-question-circle" />
            </GradeTooltip>
          ) : null}
        </FlexContainer>
      ),
      sortValue: row => row.gradeName || fallbackGradeName || '',
      fieldValue: row => (
        <TextContainer numOfClasses={numOfClasses}>
          {row.gradeName || fallbackGradeName || '—'}
        </TextContainer>
      ),
    }

    const columns = [
      {
        title: 'Classes',
        component: (
          <FlexContainer>
            <TextHiddenOverflow>Classes</TextHiddenOverflow>
            <ClassesTooltip content={<BodyCopy>{classesTooltip}</BodyCopy>}>
              <i className="fas fa-question-circle" />
            </ClassesTooltip>
          </FlexContainer>
        ),
        sortValue: row => row.teacherLastName,
        fieldValue: row => {
          const {
            hasCompletedLessonLastPeriod,
            teacherLastName,
            teacherFirstName,
            className,
          } = row
          const showDot = showNotStartedDot(row)
          const showHeart = hasCompletedLessonLastPeriod
          const showStatusIcon = showHeart || showDot
          return (
            <FlexContainer>
              <TextContainer>
                <SmallText>
                  {teacherLastName}, {teacherFirstName}
                </SmallText>
                <ClassNameContainer>
                  <IconContainer visible={showStatusIcon}>
                    {showHeart ? (
                      <SummaryHeartIcon />
                    ) : showDot ? (
                      <StatusDot />
                    ) : null}
                  </IconContainer>
                  <BigText>{className}</BigText>
                </ClassNameContainer>
              </TextContainer>
            </FlexContainer>
          )
        },
      },
      gradeColumn,
      {
        title: displayUnit5Column
          ? 'Lesson Progress for Units 1-4'
          : 'Lesson Completion',
        component: (
          <FlexContainer>
            <TextHiddenOverflow>
              {displayUnit5Column
                ? 'Lesson Progress for Units 1-4'
                : 'Lesson Completion'}
              <LessonProgressTooltip
                content={
                  <BodyCopy>
                    {displayUnit5Column ? progressCoreTooltip : progressTooltip}
                  </BodyCopy>
                }
              >
                <i className="fas fa-question-circle" />
              </LessonProgressTooltip>
            </TextHiddenOverflow>
          </FlexContainer>
        ),
        sortValue: row => row.lessonsCompleted,
        fieldValue: row => {
          const { lessonsCompletedCore, lessonCountCore } = row
          return (
            <TextContainer numOfClasses={numOfClasses}>
              {lessonsCompletedCore} of {lessonCountCore}
            </TextContainer>
          )
        },
      },
      ...(displayUnit5Column
        ? [
            {
              title: 'Lesson Progress for Bullying Unit',
              component: (
                <FlexContainer dataTestId="report-table-unit5-column">
                  <TextHiddenOverflow>
                    Lesson Progress for Bullying Unit
                    <LastLessonTooltip
                      content={<BodyCopy>{progressUnit5Tooltip}</BodyCopy>}
                    >
                      <i className="fas fa-question-circle" />
                    </LastLessonTooltip>
                  </TextHiddenOverflow>
                </FlexContainer>
              ),
              sortValue: row => row.lessonsCompletedUnit5,
              fieldValue: row => {
                let { lessonsCompletedUnit5, lessonCountUnit5 } = row
                if (lessonCountUnit5 === 0) {
                  lessonCountUnit5 = 6
                }
                return (
                  <TextContainer numOfClasses={numOfClasses}>
                    {lessonsCompletedUnit5} of {lessonCountUnit5}
                  </TextContainer>
                )
              },
            },
          ]
        : []),
      {
        title: 'Last Lesson Marked Done',
        component: (
          <FlexContainer>
            <TextHiddenOverflow>Last Lesson Marked Done</TextHiddenOverflow>
            <LastLessonTooltip
              content={<BodyCopy>{lastLessonTooltip}</BodyCopy>}
            >
              <i className="fas fa-question-circle" />
            </LastLessonTooltip>
          </FlexContainer>
        ),
        sortValue: row => {
          const { lastLessonCompleted } = row
          return lastLessonCompleted
            ? Number(lastLessonCompleted.match(/\d+/)[0]) || ''
            : ''
        },
        fieldValue: row => {
          const {
            lastLessonCompletedParent,
            lastLessonCompleted,
            lastLessonCompletedTitle,
          } = row
          return (
            <TextContainer numOfClasses={numOfClasses}>
              {!lastLessonCompleted ? (
                'Not Started Yet'
              ) : (
                <LastLessonStack>
                  <LastLessonNumLine>
                    {lastLessonCompletedParent && (
                      <>{toTitleCase(lastLessonCompletedParent)} · </>
                    )}
                    {toTitleCase(lastLessonCompleted || '')}
                  </LastLessonNumLine>
                  <LastLessonTitleLine>
                    {lessonTitleMap[lastLessonCompletedTitle] ||
                      lastLessonCompletedTitle}
                  </LastLessonTitleLine>
                </LastLessonStack>
              )}
            </TextContainer>
          )
        },
      },
      {
        title: 'Marked Done On',
        component: (
          <FlexContainer>
            <TextHiddenOverflow>Marked Done On</TextHiddenOverflow>
            <MarkedDoneTooltip
              content={<BodyCopy>{markedDoneTooltip}</BodyCopy>}
            >
              <i className="fas fa-question-circle" />
            </MarkedDoneTooltip>
          </FlexContainer>
        ),
        sortValue: row => row.lastLessonCompletedDate || '',
        fieldValue: row => {
          const lastLessonsCompletedDate = row.lastLessonCompletedDate
            ? dayjs(row.lastLessonCompletedDate).format('MM/DD/YYYY')
            : '—'
          return <MarkedDoneDate>{lastLessonsCompletedDate}</MarkedDoneDate>
        },
      },
    ]

    const summaryClassesWord = totalClassesCount === 1 ? 'Class' : 'Classes'
    const notMarkedWord =
      numClassesNotMarkedLesson === 1 ? `class hasn't` : `classes haven't`
    const markedWord = numClassesMarkedLastPeriod === 1 ? 'class' : 'classes'

    return (
      <OuterCard data-testid="report-table-v2">
        <TableLastUpdated>{lastUpdatedText}</TableLastUpdated>
        <TitleRow>
          <TitleText>{title}</TitleText>
          {hasMultipleUnitRangeOptions ? (
            <UnitRangeDropdown
              onUnitRangeChange={onUnitRangeChange}
              options={unitRangeOptionsList}
              unitRangeLabel={unitRangeLabel}
            />
          ) : (
            <UnitRangePillButton $isStatic disabled type="button">
              {staticUnitRangeDisplayLabel}
            </UnitRangePillButton>
          )}
        </TitleRow>
        {showFilterToolbar && (
          <FilterRow>
            <FilterLabel>Filter:</FilterLabel>
            <FilterButtonGroup>
              {hasInlineGradeFilter && (
                <SchoolGradeFilter
                  grades={gradeFilterGrades}
                  onToggleGrade={onGradeFilterToggle ?? (() => {})}
                  selectedGradeNames={selectedGradeNames}
                />
              )}
              {!hasInlineGradeFilter && showGradeFilter && (
                <PillButton
                  disabled={typeof onGradeFilterClick !== 'function'}
                  type="button"
                  onClick={onGradeFilterClick}
                >
                  Grade
                  <ChevronIcon aria-hidden className="fas fa-angle-down" />
                </PillButton>
              )}
              {hasInlineActivityFilter && (
                <CheckboxDropdownFilter
                  buttonLabel={activityFilterLabel}
                  dataTestId="school-summary-activity-filter"
                  idPrefix="recent-activity-filter"
                  options={activityFilterOptions}
                  onToggle={onActivityFilterToggle ?? (() => {})}
                  selectedKeys={selectedActivityKeys}
                />
              )}
              {!hasInlineActivityFilter && showActivityFilter && (
                <PillButton
                  disabled={typeof onActivityFilterClick !== 'function'}
                  type="button"
                  onClick={onActivityFilterClick}
                >
                  {activityFilterLabel}
                  <ChevronIcon aria-hidden className="fas fa-angle-down" />
                </PillButton>
              )}
              {showResetFiltersLink ? (
                <ResetFiltersLink
                  data-testid="report-table-reset-filters"
                  type="button"
                  onClick={onResetFilters}
                >
                  Reset Filters
                </ResetFiltersLink>
              ) : null}
            </FilterButtonGroup>
          </FilterRow>
        )}
        {showSummary && (
          <SummaryRow>
            <span>
              <SummaryStrong>{totalClassesCount}</SummaryStrong>
              {` ${summaryClassesWord}`}
            </span>
            <SummaryDivider aria-hidden />
            <SummaryWithIcon>
              <StatusDot aria-hidden />
              <span>
                {`${numClassesNotMarkedLesson} ${notMarkedWord} marked a lesson done yet`}
              </span>
            </SummaryWithIcon>
            <SummaryDivider aria-hidden />
            <SummaryWithIcon>
              <SummaryHeartIcon />
              <span>
                {`${numClassesMarkedLastPeriod} ${markedWord} marked a lesson done in the past 14 days`}
              </span>
            </SummaryWithIcon>
          </SummaryRow>
        )}
        <HairlineDivider aria-hidden />
        <TableScrollRegion>
          {numOfClasses === 0 ? (
            <StyledEmptyStateBox
              data-testid="report-table-v2-empty"
              icon={emptyStateIcon ?? defaultEmptyStateIcon}
              text="Try adjusting or reset your filters"
              title="No Results"
            />
          ) : (
            <ReportTableV2DataTable
              $clickableRows={typeof handleRowClick === 'function'}
              columns={columns}
              defaultSort={{ columnTitle: 'Classes', direction: 'asc' }}
              filter={false}
              handleRowClick={handleRowClick}
              headThemeLight
              key="report-table-v2-inner"
              numOfClasses={numOfClasses}
              hasUnit5={displayUnit5Column}
              ref={ref}
              rows={classes}
              sort
            />
          )}
        </TableScrollRegion>
      </OuterCard>
    )
  },
)

ReportTableV2.propTypes = {
  activityFilterLabel: PropTypes.string,
  activityFilterOptions: PropTypes.arrayOf(PropTypes.string),
  classes: PropTypes.array,
  classesTooltip: PropTypes.string,
  displayUnit5Column: PropTypes.bool,
  emptyStateIcon: PropTypes.string,
  fallbackGradeName: PropTypes.string,
  gradeFilterGrades: PropTypes.arrayOf(PropTypes.string),
  gradeTooltip: PropTypes.string,
  handleRowClick: PropTypes.func,
  lastLessonTooltip: PropTypes.string,
  lastUpdatedText: PropTypes.string,
  markedDoneTooltip: PropTypes.string,
  numClassesMarkedLastPeriod: PropTypes.number,
  numClassesNotMarkedLesson: PropTypes.number,
  onActivityFilterClick: PropTypes.func,
  onActivityFilterToggle: PropTypes.func,
  onGradeFilterClick: PropTypes.func,
  onGradeFilterToggle: PropTypes.func,
  onResetFilters: PropTypes.func,
  onUnitRangeChange: PropTypes.func,
  progressCoreTooltip: PropTypes.string,
  progressTooltip: PropTypes.string,
  progressUnit5Tooltip: PropTypes.string,
  selectedActivityKeys: PropTypes.oneOfType([
    PropTypes.instanceOf(Set),
    PropTypes.arrayOf(PropTypes.string),
  ]),
  selectedGradeNames: PropTypes.oneOfType([
    PropTypes.instanceOf(Set),
    PropTypes.arrayOf(PropTypes.string),
  ]),
  showActivityFilter: PropTypes.bool,
  showFilters: PropTypes.bool,
  showGradeFilter: PropTypes.bool,
  showSummary: PropTypes.bool,
  title: PropTypes.string,
  totalClassesCount: PropTypes.number,
  unitRangeLabel: PropTypes.string,
  /** Title-row unit range choices; `length > 1` shows a dropdown (local UI only until wired). */
  unitRangeOptions: PropTypes.arrayOf(PropTypes.string),
}

ReportTableV2.defaultProps = {
  activityFilterLabel: 'Recent Activity',
  classes: [],
  lastUpdatedText: 'Updated 1 hour ago',
  displayUnit5Column: false,
  fallbackGradeName: '',
  gradeTooltip: null,
  numClassesMarkedLastPeriod: 0,
  numClassesNotMarkedLesson: 0,
  showActivityFilter: true,
  showFilters: true,
  showGradeFilter: true,
  showSummary: true,
  title: 'Core Unit Progress by Class',
  totalClassesCount: 0,
  unitRangeLabel: 'Units 1–4',
}

export default ReportTableV2
